import React, { Fragment, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { makeStyles, Theme } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';

import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import ListIcon from '@material-ui/icons/Sort';
import TileIcon from '@material-ui/icons/BorderAll';
import useStore from '../../../store';
import { ViewStyle } from '../../../store/types';

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    borderRadius: 4,
    padding: `${theme.spacing(1) / 2}px ${theme.spacing(1)}px`,
    color: theme.palette.grey[800]
  },
  sortIcon: {
    fontSize: '24px'
  },
  selected: {
    color: 'white',
    backgroundColor: `${theme.palette.secondary.main} !important`
  },
  menuIcon: {
    marginRight: theme.spacing(2)
  },
  iconLabel: {
    color: 'black',
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5)
  },
  downIcon: {
    fontSize: '24px',
    marginLeft: -theme.spacing(0.5),
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shortest
    })
  },
  downIconRotate: {
    transform: 'rotate(180deg)',
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shortest
    })
  }
}));

const ViewControl: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation('Classrooms');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [{ ui }, { setViewStyle }] = useStore();

  const handleButtonClick: React.MouseEventHandler<HTMLButtonElement> = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMoreOptionsMenuClose = (): void => {
    setAnchorEl(null);
  };

  const handleViewChange = (style: ViewStyle) => {
    return (): void => {
      setAnchorEl(null);
      setViewStyle(style);
    };
  };

  const renderIconButton = (): React.ReactElement =>
    ui.viewStyle === ViewStyle.TILE ? (
      <TileIcon className={classes.sortIcon} />
    ) : (
      <ListIcon className={classes.sortIcon} />
    );
  const renderViewOptions = (): React.ReactNode => [
    <MenuItem
      key="list"
      selected={ui.viewStyle === ViewStyle.LIST}
      onClick={handleViewChange(ViewStyle.LIST)}
      className={clsx({ [classes.selected]: ui.viewStyle === ViewStyle.LIST })}
    >
      <ListIcon className={classes.menuIcon} />
      <Typography>{t('List View')}</Typography>
    </MenuItem>,
    <MenuItem
      key="tile"
      selected={ui.viewStyle === ViewStyle.TILE}
      onClick={handleViewChange(ViewStyle.TILE)}
      className={clsx({ [classes.selected]: ui.viewStyle === ViewStyle.TILE })}
    >
      <TileIcon className={classes.menuIcon} />
      <Typography>{t('Tile View')}</Typography>
    </MenuItem>
  ];

  return (
    <Fragment>
      <IconButton
        aria-controls="switch-view-menu"
        aria-label={t('Switch view options')}
        aria-haspopup="true"
        onClick={handleButtonClick}
        className={classes.button}
      >
        {renderIconButton()}
        <Typography className={classes.iconLabel} align="center" variant="body1">
          {t('View')}
        </Typography>
        <KeyboardArrowDownIcon color="secondary" className={clsx(classes.downIcon)} />
      </IconButton>
      <Menu
        id="switch-view-menu"
        variant="menu"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleMoreOptionsMenuClose}
      >
        {renderViewOptions()}
      </Menu>
    </Fragment>
  );
};

export default ViewControl;
